import React, { useEffect, useState } from "react";
import MainLayout from "layouts/Main";
import { Typography, Box, FormControl, Divider, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useAppDispatch, useAppSelector } from "hooks";
import Table from "components/Table";
import dayjs, { Dayjs } from "dayjs";
import { PeriodPresetTypes } from "helpers/enums";
import Select from "components/Select";
import {
  selectForecastTemplates,
  selectReportsQueryOptions,
} from "store/selectors";
import {
  fetchAllForecastTemplates,
  fetchReportsQueryOptions,
} from "store/appSlice";
import {
  buildClientReportRows,
  ClientReportRow,
} from "helpers/clientReportRows";
import {
  getAdminClientReport,
  getAdminClientReportExcelExport,
} from "api/resources/clients";
import { getClientGroups } from "api/resources/clientGroups";
import { ClientGroup } from "api/models/ClientGroup";
import PrivateClientGroupIcon from "components/PrivateClientGroupIcon";
import { getPortfolios } from "api/resources/portfolios";
import { ClientReport } from "api/models/Clients";
import downloadCSV from "helpers/excelExport";
import ReportClientSelect, { ClientOption } from "./ReportClientSelect";

interface MonthRange {
  start: Dayjs | null;
  end: Dayjs | null;
}

interface PortfolioOption {
  id: number;
  name: string;
}

const OverviewSummary = () => {
  const reportsQueryOptions = useAppSelector(selectReportsQueryOptions);
  const savedSamples = useAppSelector(selectForecastTemplates);
  const [snackbar, setSnackbar] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string | "">("");
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(
    null,
  );
  const [clientGroups, setClientGroups] = useState<ClientGroup[]>([]);
  const [selectedClientGroupId, setSelectedClientGroupId] = useState<
    number | ""
  >("");
  const [portfolios, setPortfolios] = useState<PortfolioOption[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | "">(
    "",
  );
  const [selectedSavedSampleId, setSelectedSavedSampleId] = useState<
    number | ""
  >("");
  const [tableData, setTableData] = useState<ClientReportRow[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(null);
  const [selectedMonthByPeriod, setSelectedMonthByPeriod] =
    useState<MonthRange>({
      start: null,
      end: null,
    });
  const [clientReport, setClientReport] = useState<ClientReport>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchReportsQueryOptions());
    dispatch(fetchAllForecastTemplates());
  }, [dispatch]);

  useEffect(() => {
    getClientGroups()
      .then((result) => setClientGroups(result?.data ?? []))
      .catch(() => setClientGroups([]));
  }, []);

  useEffect(() => {
    getPortfolios()
      .then((result) => setPortfolios(result?.data ?? []))
      .catch(() => setPortfolios([]));
  }, []);

  const selectedClientGroup = clientGroups.find(
    (group) => group.id === selectedClientGroupId,
  );
  const groupClientOptions: ClientOption[] | null = selectedClientGroup
    ? selectedClientGroup.clients.map((c) => ({ title: c.name, value: c.id }))
    : null;
  const selectedClientIds = selectedClient ? [selectedClient.value] : [];
  const hasClientFilter =
    selectedClientIds.length > 0 ||
    selectedClientGroupId !== "" ||
    selectedPortfolioId !== "" ||
    selectedSavedSampleId !== "";

  useEffect(() => {
    if (!startDate || !endDate || !hasClientFilter) {
      setClientReport(null);
      setTableData([]);
      return;
    }

    setLoading(true);

    getAdminClientReport(
      dayjs(startDate).format("YYYY-MM-DD"),
      dayjs(endDate).format("YYYY-MM-DD"),
      selectedClientIds,
      selectedClientGroupId || null,
      selectedPortfolioId || null,
      selectedSavedSampleId || null,
    )
      .then((result: any) => {
        setClientReport(result.data);
        setLoading(false);
      })
      .catch((error: any) => {
        console.error({ error });
        setClientReport(null);
        setTableData([]);
        setLoading(false);
      });
  }, [
    selectedClient,
    selectedClientGroupId,
    selectedPortfolioId,
    selectedSavedSampleId,
    startDate,
    endDate,
  ]);

  const handlePortfolioChange = (event: any) => {
    setSelectedPortfolioId(event.target.value);
    setSelectedClientGroupId("");
    setSelectedClient(null);
    setSelectedSavedSampleId("");
  };

  const handleClientGroupChange = (event: any) => {
    setSelectedClientGroupId(event.target.value);
    setSelectedPortfolioId("");
    setSelectedClient(null);
    setSelectedSavedSampleId("");
  };

  const handleClientChange = (client: ClientOption | null) => {
    setSelectedClient(client);
    setSelectedPortfolioId("");
    setSelectedSavedSampleId("");
  };

  const handleSavedSampleChange = (event: any) => {
    setSelectedSavedSampleId(event.target.value);
    setSelectedPortfolioId("");
    setSelectedClientGroupId("");
    setSelectedClient(null);
  };

  const snackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar(null);
  };

  const handlePeriodChange = (value: any) => {
    const newSelectedPeriod = value.props.value;

    if (newSelectedPeriod) {
      const periodStartDate = reportsQueryOptions.periodPresets.find(
        (preset) => preset.title === value.props.value,
      )?.period.from;

      const periodEndDate = reportsQueryOptions.periodPresets.find(
        (preset) => preset.title === value.props.value,
      )?.period.to;

      setStartDate(dayjs(periodStartDate));
      setEndDate(dayjs(periodEndDate));
      setSelectedPeriod(newSelectedPeriod);
    }
  };

  const handleExportDetailedReport = () => {
    setExportLoading(true);

    const fileName =
      selectedMonthByPeriod.start && selectedMonthByPeriod.end
        ? dayjs(selectedMonthByPeriod.start).format("DD.MM.YYYY") +
          "-" +
          dayjs(selectedMonthByPeriod.end).format("DD.MM.YYYY") +
          "_REPORT"
        : "REPORT";

    getAdminClientReportExcelExport(
      dayjs(selectedMonthByPeriod.start).format("YYYY-MM-DD"),
      dayjs(selectedMonthByPeriod.end).format("YYYY-MM-DD"),
      selectedClientIds,
      selectedClientGroupId || null,
      selectedPortfolioId || null,
      selectedSavedSampleId || null,
    )
      .then((result) => {
        setExportLoading(false);
        downloadCSV(result, fileName, true);
        setSnackbar({
          type: "success",
          message: "Report exported successfully",
        });
      })
      .catch((error: any) => {
        console.error({ error });
        setExportLoading(false);
        setSnackbar({
          type: "error",
          message: "Report export failed",
        });
      });
  };

  useEffect(() => {
    if (clientReport) {
      setTableData(buildClientReportRows(clientReport));
    }
  }, [clientReport]);

  const columns = [
    {
      accessorKey: "item",
      header:
        dayjs(startDate).format("DD.MM.YYYY") +
        " - " +
        dayjs(endDate).format("DD.MM.YYYY"),
      cell: ({ row }: { row: { original: ClientReportRow } }) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          {row.original.item}
          {row.original.formula && (
            <Tooltip title={row.original.formula} placement="right" arrow>
              <InfoOutlinedIcon
                sx={{ fontSize: 16, cursor: "help" }}
                color="action"
              />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      accessorKey: "value",
      header: "",
      meta: { isNumeric: true },
    },
  ];

  const handleMonthChange = (newValue: Dayjs | null) => {
    if (newValue) {
      const startOfMonth = newValue.startOf("month");
      const endOfMonth = newValue.endOf("month");

      setSelectedMonthByPeriod({ start: startOfMonth, end: endOfMonth });
    }
    setSelectedMonth(newValue);
  };

  return (
    <MainLayout
      tabs={[
        { label: "Summary", path: "OverviewSummary" },
        { label: "Charts", path: "OverviewCharts" },
      ]}
      snackbar={{
        open: !!snackbar,
        handleClose: snackbarClose,
        type: snackbar?.type,
        message: snackbar?.message || "",
      }}
    >
      <Typography variant="body2" color="textSecondary">
        Overview
      </Typography>
      <Typography variant="h5">Summary report</Typography>
      <Box maxWidth={1200}>
        <Box
          display="flex"
          flexDirection="column"
          sx={{ marginTop: 4, marginBottom: 2 }}
        >
          <Box>
            <Typography component="p" sx={{ mb: 2 }}>
              Summarized report
            </Typography>
            <FormControl>
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                <Box sx={{ width: 180 }}>
                  <Select
                    label="Portfolio"
                    id="portfolio"
                    name="portfolio"
                    minWidth={180}
                    value={selectedPortfolioId}
                    onChange={handlePortfolioChange}
                    options={portfolios.map((portfolio) => ({
                      label: portfolio.name,
                      value: portfolio.id,
                    }))}
                  />
                </Box>
                <Box sx={{ width: 180 }}>
                  <Select
                    label="Client group"
                    id="clientGroup"
                    name="clientGroup"
                    minWidth={180}
                    value={selectedClientGroupId}
                    onChange={handleClientGroupChange}
                    options={clientGroups.map((group) => ({
                      label: (
                        <>
                          {group.name}
                          {!group.visibleToClients && (
                            <PrivateClientGroupIcon />
                          )}
                        </>
                      ),
                      value: group.id,
                    }))}
                  />
                </Box>
                <ReportClientSelect
                  selectedClient={selectedClient}
                  setSelectedClient={handleClientChange}
                  groupClients={groupClientOptions}
                />
                <Box sx={{ width: 220 }}>
                  <Select
                    label="Consumption forecast"
                    id="consumptionForecast"
                    name="consumptionForecast"
                    minWidth={220}
                    value={selectedSavedSampleId}
                    onChange={handleSavedSampleChange}
                    options={(savedSamples ?? []).map((sample) => ({
                      label: sample.name,
                      value: sample.id,
                    }))}
                  />
                </Box>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
                mt={1.5}
              >
                <Box sx={{ width: 150 }}>
                  <Select
                    label="Period"
                    id="period"
                    name="period"
                    minWidth={140}
                    onChange={(event: any, value: any) =>
                      handlePeriodChange(value)
                    }
                    value={selectedPeriod}
                    options={
                      reportsQueryOptions
                        ? [...reportsQueryOptions.periodPresets].map(
                            (periodPreset) => {
                              return {
                                label:
                                  PeriodPresetTypes[
                                    periodPreset.title as keyof typeof PeriodPresetTypes
                                  ],
                                value: periodPreset.title,
                              };
                            },
                          )
                        : []
                    }
                  />
                </Box>
                <DatePicker
                  sx={{ width: 170 }}
                  slotProps={{ textField: { size: "small" } }}
                  name="startDate"
                  value={startDate}
                  disableFuture={true}
                  onChange={(value) => {
                    setStartDate(dayjs(value));
                  }}
                  label="Start date"
                />
                <DatePicker
                  sx={{ width: 170 }}
                  slotProps={{ textField: { size: "small" } }}
                  name="endDate"
                  value={endDate}
                  disableFuture={true}
                  minDate={startDate}
                  onChange={(value) => {
                    setEndDate(dayjs(value));
                  }}
                  label="End date"
                />
              </Box>
            </FormControl>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography component="p" sx={{ mb: 2 }}>
              Export detailed report
            </Typography>
            <FormControl>
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
              >
                <DatePicker
                  sx={{ mr: 1, maxWidth: "180px" }}
                  slotProps={{ textField: { size: "small" } }}
                  name="period"
                  value={selectedMonth}
                  disableFuture={true}
                  minDate={dayjs().subtract(2, "year")}
                  openTo="month"
                  views={["year", "month"]}
                  onChange={handleMonthChange}
                  label="Period"
                />
                <LoadingButton
                  variant="contained"
                  disabled={!selectedMonth || exportLoading || !hasClientFilter}
                  loading={exportLoading}
                  loadingIndicator="Loading..."
                  sx={{ ml: 1 }}
                  onClick={handleExportDetailedReport}
                >
                  Export
                </LoadingButton>
              </Box>
            </FormControl>
          </Box>
        </Box>
        {!hasClientFilter && (
          <Typography color="textSecondary" sx={{ mb: 2 }}>
            Select a portfolio, a client group, a client or a consumption
            forecast to see the report.
          </Typography>
        )}
        {startDate && endDate && hasClientFilter && (
          <Table
            isFetching={loading}
            data={tableData}
            columns={columns}
            pageCount={1}
          />
        )}
      </Box>
    </MainLayout>
  );
};

export default OverviewSummary;
